import type { ChangeEvent, FormEvent } from "react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Eye, EyeOff, Globe2, Loader2, MapPin } from "lucide-react";
import AuthLayout from "@/layouts/AuthLayout";
import SocialButtons from "@/components/SocialButtons";
import { useAuth } from "@/store/auth";
import { errorMessage } from "@/api/client";

type TouristType = "domestic" | "international";

interface FormState {
  full_name: string;
  email: string;
  phone: string;
  nationality: string;
  passport: string;
  password: string;
  confirm: string;
}

const EMPTY: FormState = {
  full_name: "",
  email: "",
  phone: "",
  nationality: "",
  passport: "",
  password: "",
  confirm: "",
};

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [touristType, setTouristType] = useState<TouristType | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const set = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = (): string | null => {
    if (form.full_name.trim().length < 2) return "Please enter your full name.";
    const phone = form.phone.replace(/\D/g, "").replace(/^91/, "");
    if (!/^[6-9]\d{9}$/.test(phone))
      return "Enter a valid 10-digit Indian mobile number.";
    if (form.password.length < 8) return "Password must be at least 8 characters.";
    if (form.password !== form.confirm) return "Passwords do not match.";
    if (!agree) return "Please accept the Terms & Conditions to continue.";
    return null;
  };

  const onSubmit = async (e: FormEvent) => {
  e.preventDefault();
  const v = validate();
  if (v) {
    setError(v);
    return;
  }
  setError(null);
  setLoading(true);
  try {
    await register({
      full_name: form.full_name.trim(),
      phone: form.phone.replace(/\D/g, "").replace(/^91/, ""),
      email: form.email.trim() || undefined,
      password: form.password,
    });
    navigate("/home", { replace: true }); // -> Home, not dashboard
  } catch (err) {
    setError(errorMessage(err, "Could not create your account"));
  } finally {
    setLoading(false);
  }
};

  // --- Step 1: choose tourist type ---
  if (!touristType) {
    return (
      <AuthLayout>
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-navy-900">
            Create Your Account
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Choose the type of tourist to personalize your experience
          </p>
        </div>

        <div className="mt-8 space-y-4">
          <TypeCard
            theme="iris"
            icon={<MapPin size={22} />}
            title="Domestic Tourist"
            subtitle="Indian Citizens & Residents"
            points={["For Indian citizens and residents", "Local travel and experiences", "Access Smart Heritage Pass"]}
            cta="Continue as Domestic Tourist"
            onClick={() => setTouristType("domestic")}
          />
          <TypeCard
            theme="jade"
            icon={<Globe2 size={22} />}
            title="International Tourist"
            subtitle="Foreign Visitors & Overseas Travelers"
            points={["For Foreign Nationals", "Visa and travel information", "Premium travel experiences"]}
            cta="Continue as International Tourist"
            onClick={() => setTouristType("international")}
          />
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-iris-600 hover:text-iris-700">
            Login
          </Link>
        </p>
      </AuthLayout>
    );
  }

  // --- Step 2: account form (themed by tourist type) ---
  const isIntl = touristType === "international";
  const submitClass = isIntl ? "btn-jade" : "btn-primary";

  return (
    <AuthLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-navy-900">
            {isIntl ? "International Tourist" : "Domestic Tourist"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Fill in the details to get started</p>
        </div>
        <button
          onClick={() => setTouristType(null)}
          className="text-xs font-semibold text-iris-600 hover:text-iris-700"
        >
          Change
        </button>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 space-y-4">
        <div>
          <label className="label">Full Name</label>
          <input value={form.full_name} onChange={set("full_name")} placeholder="Enter your full name" autoComplete="name" className="input" required />
        </div>
        <div>
          <label className="label">Email</label>
          <input type="email" value={form.email} onChange={set("email")} placeholder="Enter your email" autoComplete="email" className="input" />
        </div>
        <div>
          <label className="label">Mobile Number</label>
          <input inputMode="numeric" value={form.phone} onChange={set("phone")} placeholder="Enter your mobile number" autoComplete="tel" className="input" required />
        </div>

        {isIntl && (
          <>
            <div>
              <label className="label">Nationality</label>
              <input value={form.nationality} onChange={set("nationality")} placeholder="Enter your nationality" className="input" />
            </div>
            <div>
              <label className="label">Passport Number</label>
              <input value={form.passport} onChange={set("passport")} placeholder="Enter passport number" className="input" />
            </div>
          </>
        )}

        <div>
          <label className="label">Password</label>
          <PasswordInput value={form.password} onChange={set("password")} placeholder="Create a password" show={showPw} toggle={() => setShowPw((v) => !v)} />
        </div>
        <div>
          <label className="label">Confirm Password</label>
          <PasswordInput value={form.confirm} onChange={set("confirm")} placeholder="Confirm your password" show={showConfirm} toggle={() => setShowConfirm((v) => !v)} />
        </div>

        <label className="flex items-start gap-2.5 text-sm text-slate-600">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-iris-500 focus:ring-iris-400" />
          <span>
            I agree to the{" "}
            <a href="/#" className="font-medium text-iris-600 hover:text-iris-700">Terms &amp; Conditions</a>{" "}
            and{" "}
            <a href="/#" className="font-medium text-iris-600 hover:text-iris-700">Privacy Policy</a>
          </span>
        </label>

        <button type="submit" disabled={loading} className={`${submitClass} w-full py-3`}>
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Create Account"}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3 text-xs font-medium text-slate-400">
        <span className="h-px flex-1 bg-slate-200" />
        or continue with
        <span className="h-px flex-1 bg-slate-200" />
      </div>
      <SocialButtons />

      <p className="mt-8 text-center text-sm text-slate-500">
        Already have an account?{" "}
        <Link to="/login" className="font-semibold text-iris-600 hover:text-iris-700">
          Login
        </Link>
      </p>
    </AuthLayout>
  );
}

function TypeCard({
  theme,
  icon,
  title,
  subtitle,
  points,
  cta,
  onClick,
}: {
  theme: "iris" | "jade";
  icon: import("react").ReactNode;
  title: string;
  subtitle: string;
  points: string[];
  cta: string;
  onClick: () => void;
}) {
  const ring = theme === "iris" ? "hover:border-iris-400" : "hover:border-jade-500";
  const badge = theme === "iris" ? "bg-iris-50 text-iris-600" : "bg-jade-50 text-jade-600";
  const btn = theme === "iris" ? "btn-primary" : "btn-jade";
  return (
    <div className={`rounded-2xl border border-slate-200 p-5 transition ${ring}`}>
      <div className="flex items-center gap-3">
        <span className={`grid h-11 w-11 place-items-center rounded-xl ${badge}`}>{icon}</span>
        <div>
          <h3 className="font-display text-base font-semibold text-navy-900">{title}</h3>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {points.map((p) => (
          <li key={p} className="flex items-center gap-2 text-xs text-slate-600">
            <Check size={14} className={theme === "iris" ? "text-iris-500" : "text-jade-500"} />
            {p}
          </li>
        ))}
      </ul>
      <button onClick={onClick} className={`${btn} mt-4 w-full`}>
        {cta}
      </button>
    </div>
  );
}

function PasswordInput({
  value,
  onChange,
  placeholder,
  show,
  toggle,
}: {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  show: boolean;
  toggle: () => void;
}) {
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} autoComplete="new-password" className="input pr-11" required />
      <button type="button" onClick={toggle} className="absolute inset-y-0 right-3 grid place-items-center text-slate-400 hover:text-navy-700" aria-label={show ? "Hide password" : "Show password"}>
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}
